import { useQueryClient } from '@tanstack/react-query'
import { Divider, Flex } from 'antd'
import { Pagination } from 'antd/lib'
import { useState } from 'react'

import {
  useUserServiceGetMessagesCountEndpointKey,
  useUserServiceGetMessagesListEndpoint,
  useUserServiceGetMessagesListEndpointKey,
  useUserServiceMessagesMarkReadAllEndpoint
} from 'src/api/queries'
import { CheckIcon } from 'src/assets'
import { PageBreadcrumbs, SearchBar, Text, RangePicker } from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { resetPagination } from 'src/utils'

import { NotificationsList, SenderTypeSelect } from '../components'
import { useStyles } from '../styles'

export const Notifications = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { userData } = useUser()

  const [type, setType] = useState('')
  const [search, setSearch] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [dateFrom, setDateFrom] = useState('')

  const pagination = useTablePagination()

  const queryClient = useQueryClient()

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!type && {
      senderTypeId: type
    }),
    ...(!!dateFrom &&
      !!dateTo && {
        fromDate: dateFrom,
        toDate: dateTo
      }),
    ...(!!userData?.currentLegalEntityId && {
      legalEntityIds: [userData.currentLegalEntityId]
    })
  }

  const { data } = useUserServiceGetMessagesListEndpoint(queryParams)

  const { mutate } = useUserServiceMessagesMarkReadAllEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useUserServiceGetMessagesCountEndpointKey]
      })
      queryClient.invalidateQueries({
        queryKey: [useUserServiceGetMessagesListEndpointKey]
      })
    }
  })

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onSelectType = (val: string) => {
    setType(val)
    resetPagination(pagination)
  }

  return (
    <PageBreadcrumbs
      title={t('menu.notifications')}
      items={[
        {
          title: t('menu.notifications'),
          path: paths.roles
        }
      ]}
      extra={
        <Flex
          className={styles.readAll}
          align="center"
          gap={4}
          onClick={() => mutate()}
        >
          <CheckIcon />
          <Text>{t('notifications.read_all')}</Text>
        </Flex>
      }
    >
      <Flex vertical gap={16}>
        <Flex gap={16}>
          <SearchBar
            placeholder={t('notifications.search_placeholder')}
            onSearch={onSearch}
          />

          <SenderTypeSelect
            onChange={onSelectType}
            placeholder={t('notifications.notifications_type')}
          />

          <RangePicker
            placeholder={[
              t('notifications.from_date'),
              t('notifications.to_date')
            ]}
            setDateFrom={setDateFrom}
            setDateTo={setDateTo}
            resetPagination={() => resetPagination(pagination)}
          />
        </Flex>

        <Divider className={styles.divider} />

        <NotificationsList listData={data?.data} />

        <Pagination total={data?.totalCount} {...pagination} />
      </Flex>
    </PageBreadcrumbs>
  )
}
